import { MonoLabel, StatusPill } from '@/design-system';
import type { PublicPart } from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { JudgementPill } from './JudgementPill';
import { PublicBlock } from './PublicBlock';
import { SectionTitle } from './SectionTitle';

export interface PartsListProps {
    parts: PublicPart[];
    note: string | null;
    copy: PublicCopy;
    headingLevel: 2 | 3;
}

/**
 * Alle beoordeelde onderdelen, met nummer, naam, detail en het oordeel in zijn
 * eigen kleur, zoals in het rapport. Zonder oordeel maar met bevindingen staat
 * de ernst erbij; een onderdeel zonder oordeel en zonder bevindingen (alleen een
 * toelichting, zoals de asbestuitsluiting) krijgt geen pil: "Goed" zou daar iets
 * beweren wat niemand heeft beoordeeld.
 */
export function PartsList({ parts, note, copy, headingLevel }: PartsListProps) {
    if (parts.length === 0) {
        return null;
    }

    return (
        <PublicBlock>
            <SectionTitle level={headingLevel}>{copy.partsTitle}</SectionTitle>

            <div className="overflow-hidden rounded-[10px] border border-[var(--border-default)]">
                <ul className="m-0 list-none p-0">
                    {parts.map((part) => (
                        <li
                            key={part.number + part.label}
                            className="flex flex-wrap items-center gap-3 border-b border-[var(--border-subtle-2)] px-[14px] py-[13px]"
                        >
                            <MonoLabel
                                size={11}
                                variant="value"
                                tone="muted"
                                className="w-6 shrink-0"
                            >
                                {part.number}
                            </MonoLabel>
                            <span className="flex-[1_1_160px] text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                                {part.label}
                            </span>
                            {part.detail ? (
                                <span className="text-[12px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                                    {part.detail}
                                </span>
                            ) : null}
                            {part.judgement ? (
                                <JudgementPill label={part.judgement} tone={part.tone} />
                            ) : part.severity ? (
                                <StatusPill severity={part.severity} size="md">
                                    {copy.severityLabels[part.severity]}
                                </StatusPill>
                            ) : null}
                        </li>
                    ))}
                </ul>
                <p className="m-0 px-[14px] py-[13px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                    {note ?? copy.partsNote}
                </p>
            </div>
        </PublicBlock>
    );
}
